import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import { matches } from "@/mocks/matches";
import { seasons } from "@/mocks/seasons";
import { teams } from "@/mocks/teams";

const PAGE_SIZE = 6;

export default function Schedule() {
  const { season: seasonParam } = useParams<{ season: string }>();
  const seasonId = seasons.some((s) => s.id === seasonParam) ? seasonParam! : "2025-26";
  const currentSeason = seasons.find((s) => s.id === seasonId) ?? seasons[0];

  const [day, setDay] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const seasonMatches = useMemo(
    () =>
      matches
        .filter((m) => m.season === seasonId)
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
    [seasonId],
  );

  const daysForSeason = useMemo(() => {
    const unique = Array.from(new Set(seasonMatches.map((m) => m.date)));
    return [
      { value: "all", label: "全部日期" },
      ...unique.map((d) => ({ value: d, label: d.slice(5).replace("-", " 月 ") + " 日" })),
    ];
  }, [seasonMatches]);

  const filtered = day === "all" ? seasonMatches : seasonMatches.filter((m) => m.date === day);
  const shown = filtered.slice(0, visible);

  const statusStyle: Record<string, string> = {
    live: "bg-accent-500 text-background-50",
    upcoming: "bg-secondary-100 text-secondary-900",
    finished: "bg-background-200 text-foreground-600",
  };
  const statusLabel: Record<string, string> = {
    live: "直播中",
    upcoming: "未开始",
    finished: "已结束",
  };

  return (
    <div className="w-full">
      <PageHero
        eyebrow="SCHEDULE"
        title={`${currentSeason.name}欧冠赛程`}
        description="查看该赛季的完整赛程与比赛结果，可按比赛日期筛选，点击任意场次进入直播或比赛详情。"
        crumbs={[{ label: "首页", to: "/" }, { label: "赛程", to: `/schedule/${seasonId}` }]}
      />

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="flex flex-wrap gap-2 mb-6">
          {seasons.map((s) => (
            <Link
              key={s.id}
              to={`/schedule/${s.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                s.id === seasonId
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
              }`}
            >
              {s.name}
            </Link>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground-700 mr-1">
            <i className="ri-calendar-line text-primary-500" aria-hidden="true"></i>
            日期筛选
          </span>
          {daysForSeason.map((d) => (
            <button
              key={d.value}
              type="button"
              onClick={() => {
                setDay(d.value);
                setVisible(PAGE_SIZE);
              }}
              className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                d.value === day
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 py-16 text-center text-sm text-foreground-500">
            该日期暂无赛程
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {shown.map((m) => {
              const home = teams.find((t) => t.id === m.homeTeamId);
              const away = teams.find((t) => t.id === m.awayTeamId);
              const hasScore = m.homeScore !== null && m.awayScore !== null;
              return (
                <div
                  key={m.id}
                  className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 transition-colors hover:border-primary-300"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="lg:w-40 shrink-0">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${statusStyle[m.status]}`}
                      >
                        {m.status === "live" && (
                          <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse-live"></span>
                        )}
                        {statusLabel[m.status]}
                      </span>
                      <p className="mt-2 text-xs text-foreground-500">
                        {m.date.replace(/-/g, "/")} {m.time}
                      </p>
                    </div>

                    <div className="flex-1 flex items-center gap-3 min-w-0">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="w-9 h-9 shrink-0 rounded-md bg-background-100 flex items-center justify-center overflow-hidden">
                          <img
                            src={home?.crest}
                            alt={`${home?.name}队徽`}
                            title={`${home?.name} 欧冠赛程`}
                            className="w-full h-full object-contain"
                          />
                        </span>
                        <span className="text-sm font-medium text-foreground-900 truncate">
                          {home?.name}
                        </span>
                      </div>
                      <span className="shrink-0 font-heading text-lg font-bold text-foreground-950">
                        {hasScore ? `${m.homeScore} : ${m.awayScore}` : "VS"}
                      </span>
                      <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                        <span className="text-sm font-medium text-foreground-900 truncate text-right">
                          {away?.name}
                        </span>
                        <span className="w-9 h-9 shrink-0 rounded-md bg-background-100 flex items-center justify-center overflow-hidden">
                          <img
                            src={away?.crest}
                            alt={`${away?.name}队徽`}
                            title={`${away?.name} 欧冠赛程`}
                            className="w-full h-full object-contain"
                          />
                        </span>
                      </div>
                    </div>

                    <div className="lg:w-56 shrink-0 flex items-center gap-3 lg:justify-end">
                      <span className="text-xs text-foreground-500 truncate">{m.stage}</span>
                      {m.status === "live" && (
                        <Link
                          to={`/live/${m.id}`}
                          className="inline-flex items-center gap-1 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-background-50 hover:bg-accent-600 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-live-line" aria-hidden="true"></i>
                          直播
                        </Link>
                      )}
                      <Link
                        to={`/detail/${m.id}`}
                        className="inline-flex items-center gap-1 rounded-md border border-background-200 px-3 py-1.5 text-xs font-semibold text-primary-600 hover:bg-primary-50 transition-colors whitespace-nowrap"
                      >
                        详情
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {visible < filtered.length && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="inline-flex items-center gap-2 rounded-md border border-background-200 px-6 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              加载更多
              <i className="ri-arrow-down-line" aria-hidden="true"></i>
            </button>
          </div>
        )}
      </section>
    </div>
  );
}